import { z } from 'zod';
import { LabColorSchema } from './decors';
import { RalCodeSchema } from './paint-shared';

/**
 * Контракт `/api/paint/match` — подбор краски по фото пользователя
 * (PRD §6, ADR-0002, ADR-0006, ADR-0009, ADR-0010 §5).
 *
 * Критичные инварианты P-009 (копия decor-match.ts «защита P-009»):
 *   - На бэк уходят ТОЛЬКО `{mode, l, a, b}` — числа и literal'ы.
 *   - НЕТ поля `kind` (paint-домен один — ADR-0010 §1).
 *   - НЕТ `Blob` / `File` / `base64` / `URL` фото в запросе.
 *   - Защита: `.strict()` Zod-схема, type-уровень в `extractLab(ImageData, Region)`,
 *     ESLint allowlist расширяется на paint PhotoPicker (ADR-0010 §5),
 *     E2E network snapshot Phase 5 (`paint-p009-network-check.spec.ts`).
 *
 * Region для PhotoPicker — переиспользуется TS-тип `Region` из
 * `packages/shared/src/lab/extract.ts` (один источник истины, ADR-0009 §1).
 * Не определяется здесь, чтобы не создавать дубликат с `decor-match.ts`.
 */

// ── Mode (P-037) ──────────────────────────────────────────────────────────
// 'area' = LAB по выделенному кругу-пикеру. 'average' = LAB по среднему
// всего фото. См. ADR-0002.
export const PaintMatchModeSchema = z.enum(['area', 'average']);
export type PaintMatchMode = z.infer<typeof PaintMatchModeSchema>;

// ── Request (.strict() — ключевая защита P-009) ──────────────────────────
// `.strict()` блокирует любые посторонние поля при runtime-валидации
// (даже если type-checker обойдён, попытка передать `photoBlob` падает в
// `apiClient.paint.match()` до отправки на сеть).
//
// НЕТ `kind` — один paint-домен (ADR-0010 §1). Endpoint `/api/paint/match`
// сам несёт информацию о домене через URL.
//
// Любая попытка добавить новое поле (`photoBlob`, `mask`, `thumbnail`) →
// ОБЯЗАТЕЛЬНО обновить ADR-0009 «Откат» прежде чем снимать `.strict()`
// или расширять схему.
export const PaintMatchRequestSchema = z
  .object({
    mode: PaintMatchModeSchema,
    l: LabColorSchema.shape.l,
    a: LabColorSchema.shape.a,
    b: LabColorSchema.shape.b,
  })
  .strict();
export type PaintMatchRequest = z.infer<typeof PaintMatchRequestSchema>;

// ── Response ──────────────────────────────────────────────────────────────
// PRD §6: карточки результата с фото, RAL-кодом и дельтой ΔE. `metric`
// нужен для аналитики и будущей миграции на ΔE2000 (B-014, как в decor-match).
export const PaintMatchResponseItemSchema = z.object({
  globalId: z.string().min(1), // P-001 verbatim
  name: z.string().min(1),
  ralCode: RalCodeSchema,
  brand: z.string().min(1),
  deltaE: z.number().nonnegative(), // ΔE76 для MVP (ADR-0006)
  primaryPhotoKey: z.string().nullable(),
});
export type PaintMatchResponseItem = z.infer<typeof PaintMatchResponseItemSchema>;

export const PaintMatchResponseSchema = z.object({
  items: z.array(PaintMatchResponseItemSchema),
  metric: z.enum(['dE76', 'dE2000']),
});
export type PaintMatchResponse = z.infer<typeof PaintMatchResponseSchema>;
